// Maps a user's profile (assumptions + accounts + balances) into the
// dollar values used for each SCF cohort dimension. Pure.
//
// SCF variable definitions:
//   NETWORTH = FIN + NFIN − DEBT
//   FIN      = liquid + CDs + savings bonds + brokerage + stocks + bonds
//              + mutual funds + RETQLIQ + life insurance cash value + other
//   NFIN     = home + vehicles + business + other real estate + other NFIN
//   RETQLIQ  = IRAs + 401(k) / 403(b) / Keogh + thrift
//   HOMEEQ   = primary residence value − primary mortgage
//   LIQ      = checking + savings + money market + call accounts
//
// Cairn doesn't track vehicles or business equity separately, so
// non-financial assets here may understate vs SCF for users with
// significant vehicle/business value. That's a UX caveat, surfaced
// in the methodology footer.

import type { Account } from '../types';
import type { CohortDimension } from './schema';

export interface UserValues {
  networth: number;
  financial_assets: number;
  nonfin_assets: number;
  retirement: number;
  home_equity: number;
  liquid: number;
}

function sumWhere(
  accounts: Account[],
  balances: Record<string, number>,
  pred: (a: Account) => boolean,
): number {
  return accounts
    .filter(a => !a.archived && pred(a))
    .reduce((s, a) => s + (balances[a.id] ?? 0), 0);
}

export function computeUserValues(
  accounts: Account[],
  balances: Record<string, number>,
): UserValues {
  const hysa = sumWhere(accounts, balances, a => a.bucket === 'hysa');
  const taxable = sumWhere(accounts, balances, a => a.bucket === 'taxable');
  const retirement = sumWhere(accounts, balances, a => a.bucket === 'retirement');
  const checking = sumWhere(accounts, balances, a => a.kind === 'checking');
  const home = sumWhere(accounts, balances, a => a.kind === 'home');
  const mortgage = sumWhere(accounts, balances, a => a.kind === 'mortgage');
  const otherAsset = sumWhere(accounts, balances, a => a.kind === 'other_asset');
  const otherLiab = sumWhere(accounts, balances, a => a.kind === 'other_liability');

  // Financial assets per SCF FIN: investable + liquid + cash equivalents.
  const financial_assets = hysa + taxable + retirement + checking;
  // Non-financial: home + other tangible assets. (Vehicles/business not tracked.)
  const nonfin_assets = home + otherAsset;
  const home_equity = Math.max(0, home - mortgage);
  const liquid = hysa + checking;
  const networth = financial_assets + nonfin_assets - mortgage - otherLiab;

  return {
    networth,
    financial_assets,
    nonfin_assets,
    retirement,
    home_equity,
    liquid,
  };
}

export function dimensionLabel(dim: CohortDimension): string {
  switch (dim) {
    case 'networth':
      return 'Net worth';
    case 'financial_assets':
      return 'Financial assets';
    case 'nonfin_assets':
      return 'Non-financial assets';
    case 'retirement':
      return 'Retirement accounts';
    case 'home_equity':
      return 'Home equity';
    case 'liquid':
      return 'Liquid (cash & equivalents)';
  }
}

export function dimensionBlurb(dim: CohortDimension): string {
  switch (dim) {
    case 'networth':
      return 'Total assets minus total debts.';
    case 'financial_assets':
      return 'SCF FIN: bank accounts, CDs, brokerage, retirement accounts, life insurance cash value.';
    case 'nonfin_assets':
      return 'SCF NFIN: primary home, other real estate, vehicles, business equity. Cairn tracks home + other assets only.';
    case 'retirement':
      return 'SCF RETQLIQ: IRA, 401(k), 403(b), Keogh — the "quasi-liquid" retirement sleeve.';
    case 'home_equity':
      return 'Primary residence value minus primary mortgage balance.';
    case 'liquid':
      return 'SCF LIQ: checking, savings, money market, call accounts.';
  }
}
